import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { PortfolioTemplateDefault, PortfolioGenerator } from '../../components/portfolio';

// SEO 헤드 컴포넌트
const PortfolioSEOHead = ({ portfolio, resumeData }) => {
  if (!portfolio || !resumeData) return null;

  const { name, title: jobTitle, summary } = resumeData;
  const { slug } = portfolio;
  
  // 기본 정보 설정
  const siteUrl = window.location.origin;
  const portfolioUrl = `${siteUrl}/${slug}`; // /portfolio/ 제거
  const defaultImage = `${siteUrl}/og-default-portfolio.png`; // 기본 OG 이미지
  const profileImage = portfolio.profile_image_url || defaultImage;
  
  // 메타 정보 생성
  const metaTitle = `${name}${jobTitle ? ` - ${jobTitle}` : ''} | Latte is 웹 페이지`;
  const metaDescription = summary 
    ? summary.substring(0, 160) + (summary.length > 160 ? '...' : '')
    : `${name}님의 전문 웹 페이지를 Latte is 에서 확인해보세요. 경력, 프로젝트, 기술 스택 등 상세한 정보를 제공합니다.`;
  
  const keywords = [
    name,
    jobTitle,
    '웹페이지', 
    '이력서', 
    'portfolio', 
    'resume', 
    'latte',
    '개발자',
    '디자이너',
    '마케터',
    '취업',
    '경력'
  ].filter(Boolean).join(', ');

  return (
    <Helmet>
      {/* 기본 메타 태그 */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={name} />
      <link rel="canonical" href={portfolioUrl} />
      
      {/* Open Graph 태그 (Facebook, LinkedIn 등) */}
      <meta property="og:type" content="profile" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={portfolioUrl} />
      <meta property="og:image" content={profileImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${name}님의 프로필 사진`} />
      <meta property="og:site_name" content="latteis" />
      <meta property="og:locale" content="ko_KR" />
      
      {/* Profile 관련 Open Graph 태그 */}
      <meta property="profile:first_name" content={name?.split(' ')[0] || name} />
      <meta property="profile:last_name" content={name?.split(' ').slice(1).join(' ') || ''} />
      {jobTitle && <meta property="profile:username" content={jobTitle} />}
      
      {/* Twitter Card 태그 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={profileImage} />
      <meta name="twitter:image:alt" content={`${name}님의 프로필 사진`} />
      <meta name="twitter:site" content="@Latte is" />
      <meta name="twitter:creator" content={`@${name?.replace(/\s+/g, '')}`} />
      
      {/* LinkedIn 최적화 */}
      <meta property="article:author" content={name} />
      <meta property="article:section" content="Portfolio" />
      <meta property="article:tag" content={keywords} />
      
      {/* 모바일 최적화 */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* 웹앱 매니페스트 */}
      <meta name="theme-color" content="#1f2937" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={`${name} 웹 페이지`} />
      
      {/* robots 태그 */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* JSON-LD 구조화 데이터 */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Person",
          "name": name,
          "jobTitle": jobTitle,
          "description": metaDescription,
          "url": portfolioUrl,
          "image": profileImage,
          "sameAs": [
            portfolioUrl
          ],
          "worksFor": {
            "@type": "Organization",
            "name": "Latte is"
          },
          "alumniOf": resumeData.education?.map(edu => ({
            "@type": "EducationalOrganization",
            "name": edu.school
          })) || [],
          "knowsAbout": resumeData.skills || [],
          "hasCredential": resumeData.certificates?.map(cert => ({
            "@type": "EducationalOccupationalCredential",
            "name": cert.name,
            "credentialCategory": "certificate"
          })) || []
        })}
      </script>
    </Helmet>
  );
};

const PortfolioPage = () => {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('kr'); // 언어 상태 추가
  const [hasEnglishVersion, setHasEnglishVersion] = useState(false); // 영어 버전 존재 여부
  const [isPublishing, setIsPublishing] = useState(false); // publish 상태 변경 중

  // 포트폴리오 소유자인지 확인
  const isOwner = user && portfolio && user.id === portfolio.user_id;

  useEffect(() => {
    // Auth가 아직 로딩 중이면 기다림
    if (authLoading) {
      return;
    }
    
    const fetchPortfolio = async () => {
      // 이전 에러 상태 초기화
      setError(null);
      setLoading(true);
      
      if (!slug) {
        setError('웹페이지 주소가 올바르지 않습니다.');
        setLoading(false);
        return;
      }

      try {
        // 포트폴리오를 slug로 조회
        const { data, error: fetchError } = await supabase
          .from('portfolios')
          .select('*')
          .eq('slug', slug)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('웹페이지를 찾을 수 없습니다.');
          } else {
            setError('웹페이지를 불러오는 중 오류가 발생했습니다.');
          }
        } else {
          
          // 포트폴리오가 draft 상태이고 현재 사용자가 소유자가 아닌 경우
          if (data.status === 'draft' && (!user || user.id !== data.user_id)) {
            setError('이 웹페이지는 아직 공개되지 않았습니다.');
          } else {
            setPortfolio(data);
            
            // 영어 버전 존재 여부 확인
            if (data.session_id) {
              checkEnglishVersion(data.session_id);
            }
          }
        }
      } catch (err) {
        setError('예상치 못한 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [slug, user, authLoading]); // authLoading 의존성 추가

  // 영어 버전 존재 여부 확인
  const checkEnglishVersion = async (sessionId) => {
    try {
      const { data, error } = await supabase
        .from('resumes')
        .select('english_version')
        .eq('session_id', sessionId)
        .single();
        
      if (!error && data?.english_version) {
        setHasEnglishVersion(true);
      }
    } catch (err) {
      console.log('영어 버전 확인 중 오류:', err);
    }
  };

  // 언어 변경 핸들러
  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
  };

  // Publish 상태 토글
  const handlePublishToggle = async () => {
    if (!portfolio || !isOwner || isPublishing) return;

    const newStatus = portfolio.status === 'published' ? 'draft' : 'published';

    setIsPublishing(true);
    try {
      const { error } = await supabase
        .from('portfolios')
        .update({ status: newStatus })
        .eq('id', portfolio.id);

      if (error) throw error;

      // 성공적으로 업데이트되면 포트폴리오 상태 업데이트
      setPortfolio(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Publish 상태 변경 오류:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    } finally {
      setIsPublishing(false);
    }
  };

  // Visibility 설정 변경 핸들러 (개별 항목)
  const handleVisibilityChange = async (key, value) => {
    if (!portfolio || !isOwner) return;

    const newVisibility = {
      ...portfolio.visibility,
      [key]: value
    };

    try {
      const { error } = await supabase
        .from('portfolios')
        .update({ visibility: newVisibility })
        .eq('id', portfolio.id);

      if (error) throw error;

      // 성공적으로 업데이트되면 포트폴리오 상태 업데이트
      setPortfolio(prev => ({ ...prev, visibility: newVisibility }));
    } catch (error) {
      console.error('Visibility 설정 변경 오류:', error);
      alert('표시 설정 변경 중 오류가 발생했습니다.');
    }
  };

  // 편집 완료 후 콜백
  const handleEditComplete = (newSlug, isDeleted) => {
    setIsEditModalOpen(false);
    
    if (isDeleted) {
      // 포트폴리오가 삭제된 경우 메인 페이지로 이동
      navigate('/');
    } else if (newSlug && newSlug !== slug) {
      // 새로운 주소로 이동
      navigate(`/portfolio/${newSlug}`);
    } else {
      // 같은 주소면 포트폴리오 데이터 새로고침
      const fetchUpdatedPortfolio = async () => {
        try {
          const { data } = await supabase
            .from('portfolios')
            .select('*')
            .eq('slug', slug)
            .is('deleted_at', null)
            .single();
          if (data) {
            setPortfolio(data);
          }
        } catch (error) {
          // 오류 시 페이지 새로고침
          window.location.reload();
        }
      };
      fetchUpdatedPortfolio();
    }
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-lg text-gray-600">웹페이지를 불러오고 있습니다...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">웹페이지를 찾을 수 없습니다</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a 
            href="/"
            className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            홈으로 돌아가기
          </a>
        </div>
      </div>
    );
  }

  // 포트폴리오 렌더링
  if (!portfolio || !portfolio.resume_snapshot) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">웹페이지 데이터가 없습니다</h2>
          <p className="text-gray-600">웹페이지 정보가 완전하지 않습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* SEO 최적화 헤드 */}
      <PortfolioSEOHead 
        portfolio={portfolio}
        resumeData={portfolio?.resume_snapshot}
      />
      
      {/* 웹페이지 컨텐츠 */}
      <PortfolioTemplateDefault 
        portfolio={portfolio}
        isOwner={isOwner}
        currentLanguage={currentLanguage}
        hasEnglishVersion={hasEnglishVersion}
        onLanguageChange={handleLanguageChange}
        onEditClick={() => setIsEditModalOpen(true)}
        visibility={portfolio.visibility || {}}
        onVisibilityChange={handleVisibilityChange}
      />
      
      {/* 웹페이지 수정을 위한 PortfolioGenerator 컴포넌트 */}
      <PortfolioGenerator 
        userId={user?.id}
        sessionId={portfolio?.session_id}
        resumeData={portfolio?.resume_snapshot}
        externalModalOpen={isEditModalOpen}
        onModalClose={handleEditComplete}
        existingPortfolio={portfolio}
      />
    </div>
  );
};

export default PortfolioPage;
